import { useQuery } from "@tanstack/react-query";
import { Select } from "@/components/Selects/select";
import { type MultiValue, type SingleValue } from "react-select";
import { useTRPC } from "@/trpc";
import { Button } from "@/components/ui/button";
import { PROFILE_SELECT_CLASS_NAMES } from "./profile-select-styles";

const PROFESSIONAL_TYPES_STALE_MS = 5 * 60 * 1000;

type HealthcareProfessionalTypeValue = {
  name: string;
  id: string;
  code?: string | null;
};

type HealthcareProfessionalTypeOption = HealthcareProfessionalTypeValue & {
  label: string;
  value: string;
  aliases?: string[];
};

function formatOption(value: HealthcareProfessionalTypeValue) {
  return { ...value, label: value.name, value: value.id };
}

export function HealthcareProfessionalTypeSelect({
  value,
  onChange,
  placeholder = "Select professional type",
  disabled = false,
  isMulti = false,
  appearance = "default",
  inputId,
}: {
  value?: HealthcareProfessionalTypeValue | HealthcareProfessionalTypeValue[];
  onChange?: (
    value:
      | HealthcareProfessionalTypeValue
      | HealthcareProfessionalTypeValue[]
      | undefined,
  ) => void;
  placeholder?: string;
  disabled?: boolean;
  isMulti?: boolean;
  appearance?: "default" | "profile";
  inputId?: string;
}) {
  const trpc = useTRPC();
  const types = useQuery(
    trpc.healthcareProfessionalTypes.getAll.queryOptions(undefined, {
      staleTime: PROFESSIONAL_TYPES_STALE_MS,
    }),
  );
  const options = (types.data ?? []).map(formatOption);
  let selected;
  if (Array.isArray(value)) {
    selected = value.map(formatOption);
  } else if (value) {
    selected = formatOption(value);
  }

  function handleChange(
    option:
      | MultiValue<HealthcareProfessionalTypeOption>
      | SingleValue<HealthcareProfessionalTypeOption>,
  ) {
    if (isMulti) {
      onChange?.(
        (option as MultiValue<HealthcareProfessionalTypeOption>).map(
          ({ id, name, code }) => ({ id, name, code }),
        ),
      );
      return;
    }
    const selectedOption =
      option as SingleValue<HealthcareProfessionalTypeOption>;
    onChange?.(
      selectedOption
        ? {
            id: selectedOption.id,
            name: selectedOption.name,
            code: selectedOption.code,
          }
        : undefined,
    );
  }

  return (
    <div className="space-y-2">
      <Select<HealthcareProfessionalTypeOption, boolean>
        inputId={inputId}
        value={selected ?? null}
        options={options}
        onChange={handleChange}
        isDisabled={
          disabled || types.isPending || (types.isError && !types.data)
        }
        isLoading={types.isPending}
        placeholder={placeholder}
        isClearable
        isMulti={isMulti}
        menuPlacement="auto"
        filterOption={({ data }, search) =>
          [data.name, data.code, ...(data.aliases ?? [])]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(search.trim().toLowerCase())
        }
        classNames={
          appearance === "profile" ? PROFILE_SELECT_CLASS_NAMES : undefined
        }
      />
      {types.isError && (
        <p role="alert" className="text-xs text-red-700">
          Could not load provider types.{" "}
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-xs"
            disabled={disabled || types.isFetching}
            onClick={() => void types.refetch()}
          >
            Try again
          </Button>
        </p>
      )}
    </div>
  );
}
